import {
  alpha,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddWorkFlowDialog from '../AddWorkflowDialog';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import * as monitoringSlice from '../../../../data/monitoring.slice';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import {
  SendMessageRecipient,
  SendMessageType,
  SIMPLE_NOTIFICATIONS_WORK_QUEUE_NAME,
} from '../../../../data/models/simplified-workflow.model';
import { WorkQueue } from '../../../../data/models/work-queue.model';
import { SimplifiedWorkflowModelTransformerRegistry } from '../../../../services/simplifiedWorkflowModelTransformers';
import {
  GridCellParams,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { capitalize, lowerCase, lowerFirst } from 'lodash';
import TextSearch from '../../../controls/TextSearch';
import SearchIcon from '@mui/icons-material/Search';
import { DataGridPro } from '../../../tables/DataGridPro';
import { hasWordStartingWith } from '../../../../utils/string.utils';
import simplifiedWorkflowUtils from '../../../../services/simplifiedWorkflowUtils';
import PageHeader from '../../../controls/PageHeader';
import { useIsSuperReaderUser } from '../../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'SimpleNotifications';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  subHeader: `${PREFIX}-subHeader`,
  search: `${PREFIX}-search`,
  recipientSelect: `${PREFIX}-recipientSelect`,
  grid: `${PREFIX}-grid`,
  chip: `${PREFIX}-chip`,
  activeChip: `${PREFIX}-activeChip`,
  draftChip: `${PREFIX}-draftChip`,
  inactiveChip: `${PREFIX}-inactiveChip`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(3),
  },
  [`& .${classes.header}`]: {
    paddingBottom: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
  },
  [`& .${classes.search}`]: {
    width: 300,
    marginRight: 20,
  },
  [`& .${classes.recipientSelect}`]: {
    width: 180,
  },
  [`& .${classes.subHeader}`]: {
    justifyContent: 'space-between',
  },
  [`& .${classes.grid}`]: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    minWidth: 0,
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
    '& .MuiDataGrid-row:hover': {
      cursor: 'pointer',
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: '10px',
    },
  },
  [`& .${classes.chip}`]: {
    width: 80,
  },
  [`& .${classes.activeChip}`]: {
    backgroundColor: alpha(theme.palette.primary.main, 0.2),
    '& .MuiChip-label': {
      color: `${theme.palette.primary.main} !important`,
    },
  },
  [`& .${classes.inactiveChip}`]: {
    backgroundColor: alpha(theme.palette.lightActionDisabled54p.main, 0.1),
    '& .MuiChip-label': {
      color: `${theme.palette.lightActionDisabled54p.main} !important`,
    },
  },
  [`& .${classes.draftChip}`]: {
    backgroundColor: alpha(theme.palette.lightTextPrimary.main, 0.2),
    '& .MuiChip-label': {
      color: `${theme.palette.lightTextPrimary.main} !important`,
    },
  },
}));

interface SimpleNotificationsListData {
  workflowUUID: string;
  name: string;
  recipient: string;
  trigger: string;
  type: string;
  status: string;
}

export default function SimpleNotifications() {
  const { t } = useTranslation('common');
  const isSuperReaderUser = useIsSuperReaderUser();
  const allRecipients = t('simplenotifications.allRecipients');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [addWorkflowDialogOpen, setAddWorkflowDialogOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState(allRecipients);
  const workQueues = useAppSelector(monitoringSlice.selectWorkQueues);
  const workflows = useAppSelector(monitoringSlice.selectWorkflows);
  const loadingWorkQueues = useAppSelector(
    monitoringSlice.selectLoadingWorkQueues,
  );

  const getSimpleNotificationsQueue = (workQueues: WorkQueue[]) =>
    workQueues?.find(
      (workQueue) => workQueue.name === SIMPLE_NOTIFICATIONS_WORK_QUEUE_NAME,
    );

  useEffect(() => {
    dispatch(monitoringSlice.fetchWorkQueuesAsync());
  }, []);

  useEffect(() => {
    if (workQueues) {
      dispatch(monitoringSlice.setWorkflows([]));
      const notificationsQueue = getSimpleNotificationsQueue(workQueues);
      if (notificationsQueue) {
        dispatch(
          monitoringSlice.fetchWorkflowsAsync(
            notificationsQueue.workQueueUUID!,
          ),
        );
      }
    }
  }, [workQueues]);

  const notificationWorkQueue = getSimpleNotificationsQueue(workQueues);

  const transformerRegistry = new SimplifiedWorkflowModelTransformerRegistry();
  let workflowsListData: SimpleNotificationsListData[] = [];
  workflows?.forEach((workflow) => {
    const transformer = transformerRegistry.getTransformer(workflow.steps);
    if (transformer) {
      const sendMessageMetadata = transformer.getSendMessageMetadata(
        workflow.steps,
      );
      workflowsListData.push({
        ...sendMessageMetadata,
        workflowUUID: workflow.workflowUUID,
        name: workflow.name,
        status: workflow.state,
      });
    }
  });
  let filteredWorkflowsListData = workflowsListData;
  if (selectedRecipient !== allRecipients) {
    filteredWorkflowsListData = workflowsListData.filter((workflow) => {
      return lowerCase(workflow.recipient) === lowerCase(selectedRecipient);
    });
  }

  if (searchText) {
    filteredWorkflowsListData = filteredWorkflowsListData.filter(
      (workflow) =>
        hasWordStartingWith(workflow.name, searchText) ||
        hasWordStartingWith(
          simplifiedWorkflowUtils.getTranslationKeyForSendMessageRecipient(
            workflow.recipient as SendMessageRecipient,
          ),
          searchText,
        ) ||
        hasWordStartingWith(
          simplifiedWorkflowUtils.getTranslationKeyForSendMessageType(
            workflow.type as SendMessageType,
          ),
          searchText,
        ),
    );
  }

  const selectableRecipients = Object.values(SendMessageRecipient).map(
    (recipient) => ({
      key: recipient as string,
      value: t(
        `workflows.${simplifiedWorkflowUtils.getTranslationKeyForSendMessageRecipient(recipient)}`,
      ),
    }),
  );

  selectableRecipients.unshift({
    key: allRecipients,
    value: allRecipients,
  });

  const handleAddNotificationClick = () => {
    dispatch(monitoringSlice.setDraftWorkflow(null));
    setAddWorkflowDialogOpen(true);
  };

  const handleAddWorkflowClose = () => {
    setAddWorkflowDialogOpen(false);
  };

  const handleAddWorkflowSave = (workQueueUUID: string) => {
    navigate(`/simple-notifications/create?workQueueUUID=${workQueueUUID}`);
  };

  const handleSearchTextChange = (val: string) => {
    setSearchText(val);
  };

  const handleRecipientChange = (recipient: string) => {
    setSelectedRecipient(recipient);
  };

  const handleWorkflowClick = (params: GridRowParams) => {
    dispatch(monitoringSlice.setDraftWorkflow(null));
    navigate(
      `/simple-notifications/edit?workflowUUID=${params.row.workflowUUID}`,
    );
  };

  const renderStatus = (params: GridCellParams): React.ReactElement => {
    const className = `${params.row.status}Chip` as
      | 'draftChip'
      | 'activeChip'
      | 'inactiveChip';
    return (
      <Chip
        className={`${classes[className]} ${classes.chip}`}
        label={capitalize(params.row.status)}
      />
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('workflows.name'),
      sortable: true,
      minWidth: 250,
    },
    {
      field: 'recipient',
      headerName: t('workflows.recipient'),
      sortable: true,
      valueFormatter: (params) => {
        return t(
          `workflows.${simplifiedWorkflowUtils.getTranslationKeyForSendMessageRecipient(params.value)}`,
        );
      },
    },
    {
      field: 'trigger',
      headerName: t('workflows.trigger'),
      sortable: true,
      minWidth: 300,
    },
    {
      field: 'type',
      headerName: t('workflows.type'),
      sortable: true,
      valueFormatter: (params) => {
        return t(
          `workflows.${simplifiedWorkflowUtils.getTranslationKeyForSendMessageType(params.value)}`,
        );
      },
    },
    {
      field: 'status',
      headerName: t('workflows.status'),
      sortable: true,
      renderCell: renderStatus,
    },
  ];

  const isLoading = false;

  return (
    <StyledPaper elevation={2}>
      <Stack direction="column" className={classes.container}>
        <PageHeader title={t('simplenotifications.title')} />
        {notificationWorkQueue && (
          <>
            <Stack direction="row" className={classes.subHeader}>
              <Stack direction="row">
                <TextSearch
                  id="text-search"
                  className={classes.search}
                  icon={<SearchIcon />}
                  label={t('workflows.search')}
                  placeholder={t('simplenotifications.searchPlaceholder')}
                  currentSearchFilter={searchText}
                  onSearchChange={handleSearchTextChange}
                />
                <TextField
                  select
                  className={classes.recipientSelect}
                  label={t('workflows.recipient')}
                  variant="outlined"
                  required={true}
                  value={selectedRecipient}
                  onChange={(evt) => handleRecipientChange(evt.target.value)}>
                  {selectableRecipients?.map((recipient) => {
                    return (
                      <MenuItem value={recipient.key} key={recipient.key}>
                        {recipient.value}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Stack>

              {!isSuperReaderUser && (
                <Button
                  color="primary"
                  variant="contained"
                  disabled={!notificationWorkQueue}
                  onClick={handleAddNotificationClick}>
                  {t('simplenotifications.add')}
                </Button>
              )}
            </Stack>
            <Box className={classes.grid}>
              <DataGridPro
                disableColumnMenu={true}
                getRowId={(row) => row.workflowUUID}
                rows={filteredWorkflowsListData}
                columns={columns}
                rowCount={filteredWorkflowsListData.length}
                loading={isLoading}
                pagination={true}
                onRowClick={isSuperReaderUser ? undefined : handleWorkflowClick}
                autoHeight
              />
            </Box>
          </>
        )}
        {!loadingWorkQueues && !notificationWorkQueue && (
          <Typography variant="h3">
            {t('simplenotifications.queueNotFound', {
              queueName: SIMPLE_NOTIFICATIONS_WORK_QUEUE_NAME,
            })}
          </Typography>
        )}
      </Stack>
      <AddWorkFlowDialog
        workQueueUUID={notificationWorkQueue?.workQueueUUID ?? ''}
        open={addWorkflowDialogOpen}
        hideWorkQueueSelector={true}
        close={handleAddWorkflowClose}
        save={handleAddWorkflowSave}
      />
    </StyledPaper>
  );
}
